import {
  FILTER_OPERATORS_WITHOUT_VALUE,
  FILTER_OPERATORS_WITH_LIST_VALUE,
  isFilterTree,
  type FilterComposition,
  type FilterCondition,
  type FilterNode,
  type FilterOperator,
  type FilterTree,
} from '@ferrari/types';

/**
 * A arvore de filtro do Filter Builder (Console SPEC v1.5 §182), como funcoes
 * PURAS.
 *
 * Mora fora do componente por um motivo medido neste playbook: logica que so
 * existe dentro de JSX so se prova renderizando, e a prova vira frágil e lenta.
 * Aqui a manipulacao de arvore se testa direto, e o componente fica sendo
 * desenho.
 *
 * O que este arquivo NAO faz, de proposito: validar o filtro. A validacao mora
 * no servidor, em `validarFiltro`, e uma segunda copia aqui divergiria na
 * segunda semana. O sintoma seria a tela liberar um filtro que o servidor
 * recusa, ou pior, a tela recusar um que o servidor aceita. A tela mostra os
 * problemas que o servidor devolveu.
 */

/**
 * O rotulo de cada operador, em portugues de operacao.
 *
 * O objeto e um `Record` FECHADO sobre `FilterOperator`: operador novo no
 * pacote compartilhado quebra a compilacao aqui, em vez de aparecer na tela com
 * o nome tecnico dele.
 */
export const ROTULO_DO_OPERADOR: Readonly<Record<FilterOperator, string>> = {
  EQ: 'é igual a',
  NEQ: 'é diferente de',
  CONTAINS: 'contém',
  IN: 'está entre os valores',
  NOT_IN: 'não está entre os valores',
  GT: 'é maior que',
  GTE: 'é maior ou igual a',
  LT: 'é menor que',
  LTE: 'é menor ou igual a',
  BETWEEN: 'está entre',
  IS_EMPTY: 'está vazio',
  IS_NOT_EMPTY: 'não está vazio',
};

export const ROTULO_DA_COMPOSICAO: Readonly<Record<FilterComposition, string>> = {
  AND: 'todas as condições',
  OR: 'qualquer uma das condições',
};

/** Onde um no esta na arvore: os indices ate ele, da raiz para baixo. */
export type Caminho = readonly number[];

/** Um grupo novo, com uma condicao dentro para nunca nascer vazio. */
export function grupoInicial(campo: string): FilterTree {
  return { operator: 'AND', children: [condicaoNova(campo)] };
}

export function condicaoNova(campo: string): FilterCondition {
  return { field: campo, operator: 'EQ', value: '' };
}

export function noEm(raiz: FilterNode, caminho: Caminho): FilterNode | null {
  let atual: FilterNode = raiz;
  for (const indice of caminho) {
    if (!isFilterTree(atual)) return null;
    const proximo = atual.children[indice];
    if (proximo === undefined) return null;
    atual = proximo;
  }
  return atual;
}

/**
 * Devolve uma arvore NOVA com o no do caminho trocado.
 *
 * Sem mutacao, porque o React decide por identidade: mutar em lugar deixaria a
 * tela mostrando o filtro antigo depois de editar uma condicao, e o defeito
 * pareceria "o clique nao funcionou".
 */
export function substituirEm(raiz: FilterNode, caminho: Caminho, novo: FilterNode): FilterNode {
  if (caminho.length === 0) return novo;
  if (!isFilterTree(raiz)) return raiz;
  const [primeiro, ...resto] = caminho;
  return {
    ...raiz,
    children: raiz.children.map((filho, i) =>
      i === primeiro ? substituirEm(filho, resto, novo) : filho,
    ),
  };
}

/** Acrescenta um no dentro do GRUPO apontado pelo caminho. */
export function acrescentarEm(raiz: FilterNode, caminho: Caminho, novo: FilterNode): FilterNode {
  const alvo = noEm(raiz, caminho);
  if (alvo === null || !isFilterTree(alvo)) return raiz;
  return substituirEm(raiz, caminho, { ...alvo, children: [...alvo.children, novo] });
}

/**
 * Remove o no apontado pelo caminho.
 *
 * A RAIZ nunca e removida: a arvore sempre tem um grupo de topo, senao a tela
 * ficaria sem lugar onde acrescentar a proxima condicao e o unico caminho de
 * volta seria recarregar a pagina.
 */
export function removerEm(raiz: FilterNode, caminho: Caminho): FilterNode {
  if (caminho.length === 0) return raiz;
  const caminhoDoPai = caminho.slice(0, -1);
  const indice = caminho[caminho.length - 1]!;
  const pai = noEm(raiz, caminhoDoPai);
  if (pai === null || !isFilterTree(pai)) return raiz;
  return substituirEm(raiz, caminhoDoPai, {
    ...pai,
    children: pai.children.filter((_, i) => i !== indice),
  });
}

/**
 * Troca o operador de uma condicao AJUSTANDO o valor.
 *
 * Esta e a funcao que impede o defeito que o proprio avaliador nomeia: um
 * clique a mais deixa o valor da condicao anterior pendurado num `IS_EMPTY`, e
 * o servidor recusa com `VALOR_PROIBIDO`. O caminho inverso e igualmente
 * concreto: sair de `IS_EMPTY` para `EQ` sem valor nenhum produz
 * `VALOR_AUSENTE`.
 *
 * `value` e REMOVIDO da condicao, e nao posto como `undefined`: o corpo vira
 * JSON, e `JSON.stringify` apaga a chave com `undefined`, mas a diferenca
 * importa para quem compara os objetos no teste.
 */
export function trocarOperador(
  condicao: FilterCondition,
  operador: FilterOperator,
): FilterCondition {
  if (FILTER_OPERATORS_WITHOUT_VALUE.includes(operador)) {
    return { field: condicao.field, operator: operador };
  }
  const jaTinha = condicao.value !== undefined;
  return {
    field: condicao.field,
    operator: operador,
    value: jaTinha ? condicao.value : '',
  };
}

/**
 * Converte o texto digitado no valor que vai no corpo da requisicao.
 *
 * Erra para o lado de mandar TEXTO quando nao ha forma declarada: mandar `"3"`
 * onde o servidor espera numero recebe uma recusa clara, enquanto adivinhar um
 * numero onde o campo e texto gravaria a condicao errada em silencio. E a mesma
 * regra do formulario de mudanca de configuracao.
 */
export function interpretarValor(texto: string, operador: FilterOperator): unknown {
  if (FILTER_OPERATORS_WITHOUT_VALUE.includes(operador)) return undefined;

  const partes = texto
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (FILTER_OPERATORS_WITH_LIST_VALUE.includes(operador)) return partes;
  if (operador === 'BETWEEN') return partes.map(comoNumeroOuTexto);
  if (operador === 'GT' || operador === 'GTE' || operador === 'LT' || operador === 'LTE') {
    return comoNumeroOuTexto(texto.trim());
  }
  return texto;
}

/**
 * Numero quando o texto E um numero, e texto em todo o resto.
 *
 * Data ISO continua texto de proposito: o avaliador do servidor sabe compará-la,
 * e converter aqui perderia o formato que quem opera digitou.
 */
function comoNumeroOuTexto(valor: string): number | string {
  if (valor === '') return valor;
  const n = Number(valor);
  return Number.isFinite(n) ? n : valor;
}

/** O texto que aparece no campo, a partir do valor gravado na condicao. */
export function textoDoValor(condicao: FilterCondition): string {
  const v = condicao.value;
  if (v === undefined || v === null) return '';
  if (Array.isArray(v)) return v.map((x) => String(x)).join(', ');
  return String(v);
}

/** Quantas condicoes a arvore tem. Serve de resumo na tela. */
export function contarCondicoes(no: FilterNode): number {
  if (!isFilterTree(no)) return 1;
  return no.children.reduce((soma, filho) => soma + contarCondicoes(filho), 0);
}
